import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Layout } from "../../components";
import {
    Col,
    Divider,
    Row,
    Switch,
    Table,
    Tabs,
    Typography,
    Badge,
} from "antd";
import IOSwitch from "../../components/Switch/IOSwitch";
import Header from "../../components/Layout/Header";
import { RewardsAction } from "../../store/actions";
import { FaPen } from "react-icons/fa";
import { Utils } from "../../config";
import { Actions } from "../../components/atoms/Actions";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
} from "@mui/material";

const { TabPane } = Tabs;

const DAY_NAMES = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

const RewardsManagement = () => {
    const dispatch = useDispatch();
    const { settings, settingsConfig, dailyRewards, freePlayConfig, isLoading } = useSelector(
        (store) => store.RewardsReducer
    );

    const [activeTab, setActiveTab] = useState("daily");

    // ── Edit modal state ──────────────────────────────────────────────────────
    const [isDailyEditOpen, setIsDailyEditOpen] = useState(false);
    const [editingReward, setEditingReward] = useState(null);
    const [editAmount, setEditAmount] = useState("");

    const [isFirstPurchaseEditOpen, setIsFirstPurchaseEditOpen] = useState(false);
    const [editBonusValue, setEditBonusValue] = useState("");

    const [isReferralEditOpen, setIsReferralEditOpen] = useState(false);
    const [editRewardAmount, setEditRewardAmount] = useState("");
    const [editMaxReferrals, setEditMaxReferrals] = useState("");

    const [isFreePlayEditOpen, setIsFreePlayEditOpen] = useState(false);
    const [editMaxFreePlays, setEditMaxFreePlays] = useState("");
    const [editTimePeriodDays, setEditTimePeriodDays] = useState("");


    // ── Helpers ───────────────────────────────────────────────────────────────
    const getSettingStatus = (key) => {
        const setting = settings?.find?.((s) => s.key === key);
        return setting ? setting.status : 0;
    };

    const handleToggle = (key, currentStatus) => {
        const newStatus = currentStatus === 1 ? 0 : 1;
        dispatch(
            RewardsAction.ToggleSetting({ key, status: newStatus }, () => {
                dispatch(RewardsAction.GetSettings());
                Utils.showSnackBar({ message: "Setting updated", severity: "success" });
            })
        );
    };

    // ── Tab change ────────────────────────────────────────────────────────────
    useEffect(() => {
        dispatch(RewardsAction.GetSettings());
        dispatch(RewardsAction.GetDailyRewards());
    }, []);

    useEffect(() => {
        if (activeTab === "daily") {
            dispatch(RewardsAction.GetDailyRewards());
        } else if (activeTab === "first_purchase") {
            dispatch(RewardsAction.GetSettingsConfig("first_purchase_bonus"));
        } else if (activeTab === "referral") {
            dispatch(RewardsAction.GetSettingsConfig("referral"));
        } else if (activeTab === "free_play") {
            dispatch(RewardsAction.GetFreePlayConfig());
        }
    }, [activeTab]);

    // ── Daily Rewards handlers ────────────────────────────────────────────────
    const openDailyEdit = (record) => {
        setEditingReward(record);
        setEditAmount(String(record.amount));
        setIsDailyEditOpen(true);
    };

    const handleDailyUpdate = () => {
        dispatch(
            RewardsAction.UpdateDailyRewards(
                { rewards: [{ day: editingReward.day, amount: Number(editAmount) }] },
                () => {
                    dispatch(RewardsAction.GetDailyRewards());
                    Utils.showSnackBar({
                        message: "Daily reward updated successfully",
                        severity: "success",
                    });
                    setIsDailyEditOpen(false);
                }
            )
        );
    };

    // ── First Purchase handlers ───────────────────────────────────────────────
    const openFirstPurchaseEdit = () => {
        setEditBonusValue(String(settingsConfig?.bonusValue ?? ""));
        setIsFirstPurchaseEditOpen(true);
    };

    const handleFirstPurchaseUpdate = () => {
        dispatch(
            RewardsAction.UpdateFirstPurchaseConfig(
                { bonusValue: Number(editBonusValue) },
                () => {
                    dispatch(RewardsAction.GetSettingsConfig("first_purchase_bonus"));
                    Utils.showSnackBar({
                        message: "First Purchase Bonus updated successfully",
                        severity: "success",
                    });
                    setIsFirstPurchaseEditOpen(false);
                }
            )
        );
    };

    // ── Referral handlers ─────────────────────────────────────────────────────
    const openReferralEdit = () => {
        setEditRewardAmount(String(settingsConfig?.rewardAmount ?? ""));
        setEditMaxReferrals(String(settingsConfig?.maxReferralsPerUser ?? ""));
        setIsReferralEditOpen(true);
    };

    const handleReferralUpdate = () => {
        dispatch(
            RewardsAction.UpdateReferralConfig(
                {
                    rewardAmount: Number(editRewardAmount),
                    maxReferralsPerUser: Number(editMaxReferrals),
                },
                () => {
                    dispatch(RewardsAction.GetSettingsConfig("referral"));
                    Utils.showSnackBar({
                        message: "Referral config updated successfully",
                        severity: "success",
                    });
                    setIsReferralEditOpen(false);
                }
            )
        );
    };

    // ── Free Play handlers ────────────────────────────────────────────────────
    const openFreePlayEdit = () => {
        setEditMaxFreePlays(String(freePlayConfig?.maxFreePlays ?? ""));
        setEditTimePeriodDays(String(freePlayConfig?.timePeriodDays ?? ""));
        setIsFreePlayEditOpen(true);
    };

    const handleFreePlayUpdate = () => {
        dispatch(
            RewardsAction.UpdateFreePlayConfig(
                {
                    maxFreePlays: Number(editMaxFreePlays),
                    timePeriodDays: Number(editTimePeriodDays),
                },
                () => {
                    dispatch(RewardsAction.GetFreePlayConfig());
                    Utils.showSnackBar({
                        message: "Free Play config updated successfully",
                        severity: "success",
                    });
                    setIsFreePlayEditOpen(false);
                }
            )
        );
    };


    // ── Columns ───────────────────────────────────────────────────────────────
    const dailyColumns = [
        {
            title: "Day",
            dataIndex: "day",
            key: "day",
            render: (day) => DAY_NAMES[day - 1] || `Day ${day}`,
        },
        {
            title: "Amount",
            dataIndex: "amount",
            key: "amount",
        },
        {
            title: "Type",
            dataIndex: "type",
            key: "type",
            render: (type) => (
                <span style={{ textTransform: "capitalize" }}>{type}</span>
            ),
        },
        // {
        //     title: "Status",
        //     dataIndex: "status",
        //     key: "status",
        //     render: (status) =>
        //         status === 1 ? (
        //             <p className="tblActive">Active</p>
        //         ) : (
        //             <p className="tblIn-Active">Inactive</p>
        //         ),
        // },
        {
            title: "Action",
            dataIndex: "id",
            key: "id",
            render: (_, record) => (
                <Actions.Button
                    title="Edit Daily Reward"
                    icon={FaPen}
                    sx={{ bgcolor: "#eef7eb", color: "#54ac3f" }}
                    onClick={() => openDailyEdit(record)}
                />
            ),
        },
    ];

    // ── Header ────────────────────────────────────────────────────────────────
    const headerProps = {
        mainHeading: "Rewards Management",
        subHeading: "",
    };

    // ── Toggle header row ─────────────────────────────────────────────────────
    const TabHeader = ({ settingKey, label }) => {
        const status = getSettingStatus(settingKey);
        return (
            <Row
                align="middle"
                justify="end"
                style={{ marginBottom: "1rem", marginTop: "0.5rem" }}
            >
                <Col>
                    <Typography.Text
                        style={{ marginRight: 10, fontWeight: 500, color: "#435179" }}
                    >
                        {label}
                    </Typography.Text>
                    <IOSwitch
                        checked={status === 1}
                        onChange={() => handleToggle(settingKey, status)}
                    />
                </Col>
            </Row>
        );
    };

    return (
        <>
            <Layout.DashboardHeader
                columnSpacing={5}
                customizeStyle={{
                    display: "flex",
                    padding: 25,
                    backgroundColor: "#fffcfc",
                    marginLeft: 2,
                }}
            >
                <Header headings={headerProps} />

                <Tabs
                    className="winnersTabs"
                    defaultActiveKey="daily"
                    onChange={setActiveTab}
                >
                    {/* ── Tab 1: Daily Rewards ─────────────────────────────────────── */}
                    <TabPane tab="Daily Rewards" key="daily">
                        <TabHeader settingKey="daily_reward" label="Enable Daily Rewards" />
                        <div className="tbl_game">
                            <Table
                                dataSource={dailyRewards}
                                columns={dailyColumns}
                                rowKey="id"
                                pagination={false}
                            />
                        </div>
                    </TabPane>

                    {/* ── Tab 2: First Purchase Bonus ──────────────────────────────── */}
                    <TabPane tab="First Purchase Bonus" key="first_purchase">
                        <TabHeader
                            settingKey="first_purchase_bonus"
                            label="Enable First Purchase Bonus"
                        />
                        <div
                            style={{
                                background: "#fff",
                                borderRadius: 10,
                                padding: "24px 32px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                            }}
                        >
                            <Row gutter={[0, 16]}>
                                <Col span={24}>
                                    <Typography.Title level={5} style={{ color: "#435179", marginBottom: 4 }}>
                                        First Purchase Bonus Configuration
                                    </Typography.Title>
                                    <Divider style={{ margin: "8px 0 16px" }} />
                                </Col>
                                <Col span={12}>
                                    <Typography.Text style={{ color: "#888", fontSize: 13 }}>
                                        Bonus Value
                                    </Typography.Text>
                                    <div style={{ fontSize: 24, fontWeight: 700, color: "#54ac3f", marginTop: 4 }}>
                                        {settingsConfig?.bonusValue != null
                                            ? `${settingsConfig.bonusValue}%`
                                            : "—"}
                                    </div>
                                </Col>
                                <Col span={12} style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
                                    <Actions.Button
                                        title="Edit First Purchase Bonus"
                                        icon={FaPen}
                                        sx={{ bgcolor: "#eef7eb", color: "#54ac3f" }}
                                        onClick={openFirstPurchaseEdit}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </TabPane>

                    {/* ── Tab 3: Referral ──────────────────────────────────────────── */}
                    <TabPane tab="Referral" key="referral">
                        <TabHeader settingKey="referral" label="Enable Referral System" />
                        <div
                            style={{
                                background: "#fff",
                                borderRadius: 10,
                                padding: "24px 32px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                            }}
                        >
                            <Row gutter={[0, 16]}>
                                <Col span={24}>
                                    <Typography.Title level={5} style={{ color: "#435179", marginBottom: 4 }}>
                                        Referral Configuration
                                    </Typography.Title>
                                    <Divider style={{ margin: "8px 0 16px" }} />
                                </Col>
                                <Col span={8}>
                                    <Typography.Text style={{ color: "#888", fontSize: 13 }}>
                                        Reward Amount (coins)
                                    </Typography.Text>
                                    <div style={{ fontSize: 24, fontWeight: 700, color: "#54ac3f", marginTop: 4 }}>
                                        {settingsConfig?.rewardAmount != null
                                            ? settingsConfig.rewardAmount
                                            : "—"}
                                    </div>
                                </Col>
                                <Col span={8}>
                                    <Typography.Text style={{ color: "#888", fontSize: 13 }}>
                                        Max Referrals Per User
                                    </Typography.Text>
                                    <div style={{ fontSize: 24, fontWeight: 700, color: "#435179", marginTop: 4 }}>
                                        {settingsConfig?.maxReferralsPerUser != null
                                            ? settingsConfig.maxReferralsPerUser
                                            : "—"}
                                    </div>
                                </Col>
                                <Col span={8} style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                                    <Actions.Button
                                        title="Edit Referral Config"
                                        icon={FaPen}
                                        sx={{ color: "#54ac3f" }}
                                        onClick={openReferralEdit}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </TabPane>

                    {/* ── Tab 4: Free Play Limit ───────────────────────────────────── */}
                    <TabPane tab="Free Play Limit" key="free_play">
                        <TabHeader settingKey="free_play_limit" label="Enable Free Play Limit" />
                        <div
                            style={{
                                background: "#fff",
                                borderRadius: 10,
                                padding: "24px 32px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                            }}
                        >
                            <Row gutter={[0, 16]}>
                                <Col span={24}>
                                    <Typography.Title level={5} style={{ color: "#435179", marginBottom: 4 }}>
                                        Free Play Limit Configuration
                                    </Typography.Title>
                                    <Divider style={{ margin: "8px 0 16px" }} />
                                </Col>
                                <Col span={8}>
                                    <Typography.Text style={{ color: "#888", fontSize: 13 }}>
                                        Max Free Plays
                                    </Typography.Text>
                                    <div style={{ fontSize: 24, fontWeight: 700, color: "#54ac3f", marginTop: 4 }}>
                                        {freePlayConfig?.maxFreePlays != null
                                            ? freePlayConfig.maxFreePlays
                                            : "—"}
                                    </div>
                                </Col>
                                <Col span={8}>
                                    <Typography.Text style={{ color: "#888", fontSize: 13 }}>
                                        Time Period (days)
                                    </Typography.Text>
                                    <div style={{ fontSize: 24, fontWeight: 700, color: "#435179", marginTop: 4 }}>
                                        {freePlayConfig?.timePeriodDays != null
                                            ? freePlayConfig.timePeriodDays
                                            : "—"}
                                    </div>
                                </Col>
                                <Col span={8} style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                                    <Actions.Button
                                        title="Edit Free Play Config"
                                        icon={FaPen}
                                        sx={{ color: "#54ac3f" }}
                                        onClick={openFreePlayEdit}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </TabPane>
                </Tabs>

            </Layout.DashboardHeader>

            {/* ── Edit Daily Reward Modal ─────────────────────────────────────────── */}
            <Dialog
                open={isDailyEditOpen}
                onClose={() => setIsDailyEditOpen(false)}
                sx={{
                    ".MuiPaper-root": {
                        borderRadius: "10px",
                        width: "min(100%, 420px)",
                        margin: "1rem",
                        py: 2,
                    },
                }}
            >
                <DialogTitle>
                    <Typography.Title level={5} style={{ margin: 0, color: "#435179" }}>
                        Edit Daily Reward — {editingReward ? DAY_NAMES[editingReward.day - 1] : ""}
                    </Typography.Title>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} pt={1}>
                        <TextField
                            label="Amount (coins)"
                            type="number"
                            fullWidth
                            value={editAmount}
                            onChange={(e) => setEditAmount(e.target.value)}
                            inputProps={{ min: 0 }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    "&.Mui-focused fieldset": { borderColor: "#54ac3f" },
                                },
                                "& .MuiInputLabel-root.Mui-focused": { color: "#54ac3f" },
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", pb: 2, gap: 1.5 }}>
                    <Button
                        onClick={() => setIsDailyEditOpen(false)}
                        sx={{
                            flex: 1,
                            maxWidth: "130px",
                            height: "2.5rem",
                            backgroundColor: "#54ac3f",
                            borderRadius: "5px",
                            color: "white",
                            textTransform: "capitalize",
                            "&:hover": { backgroundColor: "#479335", color: "white" },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDailyUpdate}
                        sx={{
                            flex: 1,
                            maxWidth: "130px",
                            height: "2.5rem",
                            backgroundColor: "#54ac3f",
                            borderRadius: "5px",
                            color: "white",
                            textTransform: "capitalize",
                            "&:hover": { backgroundColor: "#479335", color: "white" },
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Edit First Purchase Bonus Modal ──────────────────────────────────── */}
            <Dialog
                open={isFirstPurchaseEditOpen}
                onClose={() => setIsFirstPurchaseEditOpen(false)}
                sx={{
                    ".MuiPaper-root": {
                        borderRadius: "10px",
                        width: "min(100%, 420px)",
                        margin: "1rem",
                        py: 2,
                    },
                }}
            >
                <DialogTitle>
                    <Typography.Title level={5} style={{ margin: 0, color: "#435179" }}>
                        Edit First Purchase Bonus
                    </Typography.Title>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} pt={1}>
                        <TextField
                            label="Bonus Value (%)"
                            type="number"
                            fullWidth
                            value={editBonusValue}
                            onChange={(e) => setEditBonusValue(e.target.value)}
                            inputProps={{ min: 0, max: 100 }}
                            InputProps={{
                                endAdornment: (
                                    <span style={{ color: "#888", fontWeight: 600 }}>%</span>
                                ),
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    "&.Mui-focused fieldset": { borderColor: "#54ac3f" },
                                },
                                "& .MuiInputLabel-root.Mui-focused": { color: "#54ac3f" },
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", pb: 2, gap: 1.5 }}>
                    <Button
                        onClick={() => setIsFirstPurchaseEditOpen(false)}
                        sx={{
                            flex: 1,
                            maxWidth: "130px",
                            height: "2.5rem",
                            backgroundColor: "#54ac3f",
                            borderRadius: "5px",
                            color: "white",
                            textTransform: "capitalize",
                            "&:hover": { backgroundColor: "#479335", color: "white" },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleFirstPurchaseUpdate}
                        sx={{
                            flex: 1,
                            maxWidth: "130px",
                            height: "2.5rem",
                            backgroundColor: "#54ac3f",
                            borderRadius: "5px",
                            color: "white",
                            textTransform: "capitalize",
                            "&:hover": { backgroundColor: "#479335", color: "white" },
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Edit Referral Config Modal ────────────────────────────────────────── */}
            <Dialog
                open={isReferralEditOpen}
                onClose={() => setIsReferralEditOpen(false)}
                sx={{
                    ".MuiPaper-root": {
                        borderRadius: "10px",
                        width: "min(100%, 420px)",
                        margin: "1rem",
                        py: 2,
                    },
                }}
            >
                <DialogTitle>
                    <Typography.Title level={5} style={{ margin: 0, color: "#435179" }}>
                        Edit Referral Configuration
                    </Typography.Title>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} pt={1}>
                        <TextField
                            label="Reward Amount (coins)"
                            type="number"
                            fullWidth
                            value={editRewardAmount}
                            onChange={(e) => setEditRewardAmount(e.target.value)}
                            inputProps={{ min: 0 }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    "&.Mui-focused fieldset": { borderColor: "#54ac3f" },
                                },
                                "& .MuiInputLabel-root.Mui-focused": { color: "#54ac3f" },
                            }}
                        />
                        <TextField
                            label="Max Referrals Per User"
                            type="number"
                            fullWidth
                            value={editMaxReferrals}
                            onChange={(e) => setEditMaxReferrals(e.target.value)}
                            inputProps={{ min: 0 }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    "&.Mui-focused fieldset": { borderColor: "#54ac3f" },
                                },
                                "& .MuiInputLabel-root.Mui-focused": { color: "#54ac3f" },
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", pb: 2, gap: 1.5 }}>
                    <Button
                        onClick={() => setIsReferralEditOpen(false)}
                        sx={{
                            flex: 1,
                            maxWidth: "130px",
                            height: "2.5rem",
                            backgroundColor: "#54ac3f",
                            borderRadius: "5px",
                            color: "white",
                            textTransform: "capitalize",
                            "&:hover": { backgroundColor: "#479335", color: "white" },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleReferralUpdate}
                        sx={{
                            flex: 1,
                            maxWidth: "130px",
                            height: "2.5rem",
                            backgroundColor: "#54ac3f",
                            borderRadius: "5px",
                            color: "white",
                            textTransform: "capitalize",
                            "&:hover": { backgroundColor: "#479335", color: "white" },
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Edit Free Play Config Modal ───────────────────────────────────────── */}
            <Dialog
                open={isFreePlayEditOpen}
                onClose={() => setIsFreePlayEditOpen(false)}
                sx={{
                    ".MuiPaper-root": {
                        borderRadius: "10px",
                        width: "min(100%, 420px)",
                        margin: "1rem",
                        py: 2,
                    },
                }}
            >
                <DialogTitle>
                    <Typography.Title level={5} style={{ margin: 0, color: "#435179" }}>
                        Edit Free Play Configuration
                    </Typography.Title>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} pt={1}>
                        <TextField
                            label="Max Free Plays"
                            type="number"
                            fullWidth
                            value={editMaxFreePlays}
                            onChange={(e) => setEditMaxFreePlays(e.target.value)}
                            inputProps={{ min: 0 }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    "&.Mui-focused fieldset": { borderColor: "#54ac3f" },
                                },
                                "& .MuiInputLabel-root.Mui-focused": { color: "#54ac3f" },
                            }}
                        />
                        <TextField
                            label="Time Period (days)"
                            type="number"
                            fullWidth
                            value={editTimePeriodDays}
                            onChange={(e) => setEditTimePeriodDays(e.target.value)}
                            inputProps={{ min: 0 }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    "&.Mui-focused fieldset": { borderColor: "#54ac3f" },
                                },
                                "& .MuiInputLabel-root.Mui-focused": { color: "#54ac3f" },
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", pb: 2, gap: 1.5 }}>
                    <Button
                        onClick={() => setIsFreePlayEditOpen(false)}
                        sx={{
                            flex: 1,
                            maxWidth: "130px",
                            height: "2.5rem",
                            backgroundColor: "#54ac3f",
                            borderRadius: "5px",
                            color: "white",
                            textTransform: "capitalize",
                            "&:hover": { backgroundColor: "#479335", color: "white" },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleFreePlayUpdate}
                        sx={{
                            flex: 1,
                            maxWidth: "130px",
                            height: "2.5rem",
                            backgroundColor: "#54ac3f",
                            borderRadius: "5px",
                            color: "white",
                            textTransform: "capitalize",
                            "&:hover": { backgroundColor: "#479335", color: "white" },
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

        </>
    );
};

export default RewardsManagement;
